import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import useAuth from "./../hooks/useAuth";
import Preloader from "../shared/PreLoaders";

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  hours = hours % 12 || 12; // convert 0 → 12

  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const Login = () => {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpArray, setOtpArray] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(180);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fallbackOtp, setFallbackOtp] = useState(null);
  const [otpMessage, setOtpMessage] = useState(null);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [blockedUntil, setBlockedUntil] = useState(null);

  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const Api = process.env.REACT_APP_API;

  const { setAuth, fetchPermissions } = useAuth();

  /* ---------------- TIMER ---------------- */
  useEffect(() => {
    if (otpSent && timer > 0) {
      const interval = setInterval(() => setTimer(t => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [otpSent, timer]);

  /* ---------------- HANDLERS ---------------- */
  const handleChange = e => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  // ✅ Handle Login Submission with proper error handling and OTP fallback
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);
  //   setError(null);
  //   setBlockedUntil(null);

  //   try {
  //     const res = await fetch(`${Api}/api/auth/login`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(credentials),
  //     });

  //     // ✅ IMPORTANT: Always read JSON first
  //     const data = await res.json();

  //     // ❌ Handle backend errors properly
  //     if (!res.ok) {
  //       if (data.blockedUntil) {
  //         setBlockedUntil(data.blockedUntil);
  //       }
  //       setError(data.error || "Login failed");
  //       return;
  //     }

  //     // ✅ Login success → OTP sent
  //     setOtpSent(true);
  //     setTimer(180);

  //     // 👇 SHOW OTP ONLY IF EMAIL FAILED
  //     if (data.warning && data.otp) {
  //       setFallbackOtp(data.otp);
  //     } else {
  //       setFallbackOtp(null);
  //     }

  //     // 👇 Email success message
  //     if (data.message && !data.otp) {
  //       setOtpMessage(data.message);
  //     }

  //   } catch (err) {
  //     // 🔥 Network / unexpected errors only
  //     setError(err.message || "Something went wrong");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${Api}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Login failed");
      }

      // ✅ DIRECT LOGIN (NO OTP)
      setAuth(data);

      localStorage.setItem("authData", JSON.stringify(data));
      sessionStorage.setItem("authData", JSON.stringify(data));
      sessionStorage.setItem("authToken", data.token);

      fetchPermissions(data.token);

      navigate("/dashboard");

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${Api}/api/auth/send-login-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: credentials.email }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setOtpMessage(data.message);
      setTimer(30);
      setOtpArray(Array(6).fill(""));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (/^\d$/.test(value)) {
      const updatedOtpArray = [...otpArray];
      updatedOtpArray[index] = value;
      setOtpArray(updatedOtpArray);

      // Move focus to the next input if available
      if (index < otpArray.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    } else if (value === "") {
      // Handle backspace
      const updatedOtpArray = [...otpArray];
      updatedOtpArray[index] = "";
      setOtpArray(updatedOtpArray);

      // Move focus to the previous input if available
      if (index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  // Handle Paste Event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const updatedOtpArray = pastedData.split("");
      setOtpArray(updatedOtpArray);

      // Move focus to the last field
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const otp = otpArray.join("");
      const response = await fetch(`${Api}/api/auth/verify-login-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: credentials.email, otp }),
      });

      if (!response.ok) {
        throw new Error("OTP verification failed");
      }

      const data = await response.json();
      setAuth(data);

      localStorage.setItem("authData", JSON.stringify(data));
      sessionStorage.setItem("authData", JSON.stringify(data));
      sessionStorage.setItem("authToken", data.token);

      fetchPermissions(data.token);
      navigate("/dashboard");

      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };
  return (
    <div className="login-wrapper">
      {loading && <Preloader loading={loading} />}
      <div className="login-card">

        {/* LEFT */}
        <div className="login-form-section">
          <div className="logo-block">
            <img src={require("../../assets/images/logo_dark.png")} alt="TNFD" />
            <h3 className="mt-3">Joint Forest Management Committe</h3>
          </div>

          <h4 className="signin-title mt-3">
            {otpSent ? "VERIFY OTP" : "SIGN IN"}
          </h4>

          {error && <p className="error">{error}</p>}
          {otpMessage && (
            <div
              className={`alert ${fallbackOtp ? "alert-warning" : "alert-success"
                } text-center mt-3`}
            >
              {otpMessage}
              {fallbackOtp && (
                <>
                  <br />
                  <strong>OTP:</strong> {fallbackOtp}
                </>
              )}
            </div>
          )}
          {blockedUntil && (
            <div className="alert alert-danger text-center">
              Account blocked until <br />
              <strong>{formatDate(blockedUntil)}</strong>
            </div>
          )}

          <form onSubmit={otpSent ? handleVerifyOTP : handleSubmit}>

            {!otpSent && (
              <>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={credentials.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="password-field">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      required
                    />
                    {passwordVisible ? (
                      <FaEyeSlash
                        className="toggle-password"
                        onClick={() => setPasswordVisible(false)}
                      />
                    ) : (
                      <FaEye
                        className="toggle-password"
                        onClick={() => setPasswordVisible(true)}
                      />
                    )}

                  </div>
                </div>
              </>
            )}

            {otpSent && (
              <>
                <div className="otp-timer">
                  Time remaining: {Math.floor(timer / 60)}:
                  {String(timer % 60).padStart(2, "0")}
                </div>

                <div className="otp-input">
                  {otpArray.map((d, i) => (
                    <input
                      key={i}
                      ref={el => (inputRefs.current[i] = el)}
                      value={d}
                      maxLength="1"
                      onChange={e => handleOtpChange(e.target.value, i)}
                      onPaste={handlePaste}
                      disabled={error?.includes("locked")}
                    />
                  ))}
                </div>
              </>
            )}

            <button
              type="submit"
              className="login-btn"
              disabled={!!blockedUntil}
            >
              {otpSent ? "Verify OTP" : "Sign in"}
            </button>
            {otpSent && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </button>
              </div>
            )}

          </form>
          <div className="text-center mt-4 font-weight-light">
            Change Password?{" "}
            <Link to="/user-pages/forgot-password">Change</Link>
          </div>
        </div>

        {/* RIGHT */}
        <div className="login-image-section">
          <img src={require("../../assets/images/auth/login.jpg")} alt="Forest" />
        </div>

      </div>

    </div>

  );
};

export default Login;
